import { useRef, useState } from 'react';
import { Link } from 'react-router';
import type { OrderLine, OrderListItem } from '@/api/types';
import { Tag, Dot, Badge } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { changeState, invoiceStatus, orderStatus, stockStatus } from '@/lib/status';
import { daysUntil, qty, shortDate, date, money } from '@/lib/format';
import s from './OrderCards.module.css';

/** 订单详情摘要：套装按 bundleInstanceId 合并成一行（文档 23 §5.2 第③行） */
export function summarizeLines(lines: OrderLine[]) {
  const out: { kind: '套装' | '单品'; name: string; qty: string }[] = [];
  const seen = new Set<string>();
  for (const l of lines) {
    if (l.bundleInstanceId || l.bundleId) {
      const key = l.bundleInstanceId || `${l.bundleId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ kind: '套装', name: l.bundle?.name || l.sourceBundleName || '套装', qty: `${qty(l.bundleQuantity ?? 0)} 套` });
    } else {
      out.push({ kind: '单品', name: l.product.name, qty: `${qty(l.quantity)} 个` });
    }
  }
  return out;
}

export function Due({ value }: { value?: string | null }) {
  const d = daysUntil(value);
  if (d === null) return null;
  if (d < 0) return <span className={`${s.due} ${s.bad}`}>逾期 {-d} 天</span>;
  if (d === 0) return <span className={`${s.due} ${s.bad}`}>今天</span>;
  if (d === 1) return <span className={`${s.due} ${s.warn}`}>明天</span>;
  if (d <= 5) return <span className={`${s.due} ${s.warn}`}>剩 {d} 天</span>;
  return <span className={s.muted}>剩 {d} 天</span>;
}

export type WorkCardSettings = {
  compact: boolean;
  people: boolean;
  lines: boolean;
  due: boolean;
  orderDate: boolean;
  shippingMethod: boolean;
  reminders: boolean;
  stock: boolean;
  platformNo: boolean;
};

export const DEFAULT_WORK_CARD_SETTINGS: WorkCardSettings = {
  compact: false, people: true, lines: true, due: true, orderDate: false,
  shippingMethod: false, reminders: true, stock: true, platformNo: true,
};

/** 提醒行：修改记录 / 异常徽标 / 库存色点 / 1688 单号 */
function Reminders({ o, settings }: { o: OrderListItem; settings: WorkCardSettings }) {
  const cs = changeState(o.changeRequests?.[0]);
  const openEx = o.exceptions.filter((e) => e.status === 'PENDING' || e.status === 'PROCESSING').length;
  const st = o.stockStatus ? stockStatus[o.stockStatus.status] : null;
  if (!(settings.reminders && (cs || openEx > 0)) && !(settings.stock && st) && !settings.platformNo) return null;
  return (
    <div className={s.f}>
      {settings.reminders && cs && <Tag tone={cs.tone} sm>{cs.label}</Tag>}
      {settings.reminders && openEx > 0 && <Badge>{openEx} 个异常</Badge>}
      {settings.stock && st && <Dot tone={st.dot}>{st.label}</Dot>}
      {settings.platformNo && <span className={s.no}>{o.platformOrderNo || '未填 1688 单号'}</span>}
    </div>
  );
}

/** 工作卡：先状态后内容，4 行 + 提醒行（文档 23 §5.2） */
export function WorkCard({ o, selected, to, selectable, checked, onSelect, onLongPress, settings = DEFAULT_WORK_CARD_SETTINGS }: { o: OrderListItem; selected?: boolean; to: string; selectable?: boolean; checked?: boolean; onSelect?: () => void; onLongPress?: () => void; settings?: WorkCardSettings }) {
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const cancelHold = () => { if (holdTimer.current) clearTimeout(holdTimer.current); holdTimer.current = null; startPoint.current = null; };
  const lines = summarizeLines(o.lines);
  const st = orderStatus[o.status];
  const content = <>
      {selectable && <span className={`${s.check} ${checked ? s.checked : ''}`} aria-hidden="true">{checked && <Icon name="check" size={14} />}</span>}
      <div className={s.h}>
        <b className="tnum">{o.orderNo}</b>
        <Tag tone={st.tone} sm>{st.label}</Tag>
      </div>
      <div className={s.c}>{o.customer.name}</div>
      {settings.lines && lines.length > 0 && (
        <ul className={s.l}>
          {lines.slice(0, settings.compact ? 1 : 2).map((l, i) => (
            <li key={i}><i>{l.kind}</i><span>{l.name}</span><b>{l.qty}</b></li>
          ))}
          {lines.length > 2 && !settings.compact && <li className={s.more}>+{lines.length - 2} 项</li>}
        </ul>
      )}
      {(settings.due || settings.orderDate || settings.shippingMethod || (settings.people && !settings.compact)) && <div className={s.d}>
        {settings.due && <><span><i>交货</i><b>{shortDate(o.deliveryDate)}</b></span><Due value={o.deliveryDate} /></>}
        {settings.orderDate && <span><i>下单</i><b>{shortDate(o.customerOrderDate)}</b></span>}
        {settings.shippingMethod && <span><i>运输</i>{o.shippingMethod || '—'}</span>}
        {settings.people && !settings.compact && <span className={s.who}>
          {o.salesUser && <><i>业务</i>{o.salesUser.name}</>}
          <i className={s.gap}>跟单</i>{o.followerUser?.name || <span className={s.muted}>未分配</span>}
        </span>}
      </div>}
      <Reminders o={o} settings={settings} />
    </>;
  const className = [s.card, settings.compact && s.compact, selected && s.sel, selectable && s.selectable, checked && s.sel].filter(Boolean).join(' ');
  return selectable ? <button type="button" data-order-id={o.id} className={className} aria-label={`${checked ? '取消选择' : '选择'}订单 ${o.orderNo}`} aria-pressed={!!checked} onClick={onSelect}>{content}</button>
    : <Link to={to} data-order-id={o.id} className={className}
      onPointerDown={(event) => { if (!onLongPress || event.pointerType === 'mouse') return; cancelHold(); held.current = false; startPoint.current = { x: event.clientX, y: event.clientY }; holdTimer.current = setTimeout(() => { held.current = true; onLongPress(); }, 500); }}
      onPointerMove={(event) => { if (startPoint.current && Math.hypot(event.clientX - startPoint.current.x, event.clientY - startPoint.current.y) > 8) cancelHold(); }}
      onPointerUp={cancelHold} onPointerCancel={cancelHold} onPointerLeave={cancelHold}
      onClick={(event) => { if (held.current) { event.preventDefault(); held.current = false; } }}
      onContextMenu={(event) => { if (onLongPress) event.preventDefault(); }}>{content}</Link>;
}

/** 台账卡：「全部订单」视图，前 11 个字段严格按锁定列序（scripts/order-list-column-order-check.ts） */
export function LedgerCard({ o, to }: { o: OrderListItem; to: string }) {
  const [expanded, setExpanded] = useState(false);
  const st = orderStatus[o.status];
  const cs = changeState(o.changeRequests?.[0]);
  const detail = summarizeLines(o.lines).map((l) => `${l.kind}: ${l.name}-${l.qty.replace(' ', '')}`).join('；');
  const openExceptions = o.exceptions.filter((item) => item.status === 'PENDING' || item.status === 'PROCESSING').length;
  return (
    <article className={s.card}>
      <Link to={to} className={s.ledgerLink}><div className={s.g}>
        <div className={s.w}><i>1688订单号</i><b className={s.big}>{o.platformOrderNo || '—'}</b></div>
        {o.salesUser !== undefined && <div><i>业务员</i><b>{o.salesUser?.name || '—'}</b></div>}
        <div><i>客户名称</i><b>{o.customer.name}</b></div>
        <div><i>下单日期</i><b>{date(o.customerOrderDate)}</b></div>
        <div><i>最迟交货</i><b>{date(o.deliveryDate)}</b></div>
        <div><i>订单状态</i><Tag tone={st.tone} sm>{st.label}</Tag></div>
        <div><i>修改记录</i>{cs ? <Tag tone={cs.tone} sm>{cs.label}</Tag> : <b className="em">—</b>}</div>
        <div className={s.w}><i>运输方式</i><b>{o.shippingMethod || '—'}</b></div>
        <div className={s.w}><i>订单详情</i><b>{detail || '—'}</b></div>
        <div><i>跟单</i><b>{o.followerUser?.name || '—'}</b></div>
        <div><i>订单号</i><b>{o.orderNo}</b></div>
      </div></Link>
      <button type="button" className={s.expand} aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}><span>其余字段 · 店铺、开票、异常、仓库、库存、金额…</span><b>{expanded ? '收起' : '展开'} <Icon name={expanded ? 'chevu' : 'chevd'} size={14} /></b></button>
      {expanded && <div className={s.extra}>
        <div><i>店铺</i><b>{o.shopName || '—'}</b></div>
        <div><i>开票</i><b>{invoiceStatus[o.invoiceStatus || ''] || '—'}</b></div>
        <div><i>异常</i><b>{openExceptions ? `${openExceptions} 个未闭环` : o.exceptions.length ? `${o.exceptions.length} 个已闭环` : '—'}</b></div>
        <div><i>仓库</i><b>{o.warehouse?.name || '—'}</b></div>
        <div><i>库存状态</i><b>{o.stockStatus ? stockStatus[o.stockStatus.status].label : '—'}</b></div>
        <div><i>提醒</i><b>{cs?.label || (openExceptions ? `${openExceptions} 个异常` : '—')}</b></div>
        <div className={s.w}><i>备注</i><b>{o.note || '—'}</b></div>
        {o.totalAmount !== undefined && <div><i>金额</i><b>{money(o.totalAmount)}</b></div>}
      </div>}
    </article>
  );
}
